import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useApi } from '../hooks/useApi';
import { TEAMS, ALL_MATCHES } from '../lib/matches';
import { format } from 'date-fns';

const M1_KO    = new Date('2026-06-11T19:00:00Z');
const R32_START = new Date('2026-06-28T19:00:00Z');

export default function Dashboard() {
  const { user } = useAuth();
  const api = useApi();

  const [predictions, setPredictions] = useState([]);
  const [winner, setWinner] = useState('');
  const [savedWinner, setSavedWinner] = useState('');
  const [defaultHome, setDefaultHome] = useState('');
  const [defaultAway, setDefaultAway] = useState('');
  const [savedDefault, setSavedDefault] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState('');
  const [msg, setMsg] = useState({ type: '', text: '' });
  const [leagueRequestName, setLeagueRequestName] = useState('');
  const [leagueRequesting, setLeagueRequesting] = useState(false);
  const [results, setResults] = useState([]);
  const navigate = useNavigate();

  const now = new Date();
  const winnerLocked = now >= M1_KO;
  const winnerChangeable = now < R32_START;

  useEffect(() => {
    api.getResults().catch(() => []).then(r => setResults(r));
    api.getPredictions(user.sub).then(preds => {
      setPredictions(preds);
      const w = preds.find(p => p.sk === 'WINNER');
      const d = preds.find(p => p.sk === 'DEFAULT');
      if (w) { setSavedWinner(w.winner); setWinner(w.winner); }
      if (d) {
        setSavedDefault(d);
        setDefaultHome(String(d.homeScore));
        setDefaultAway(String(d.awayScore));
      }
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []); // eslint-disable-line

  const matchPreds = predictions.filter(p => p.sk?.startsWith('PRED#'));
  const jokersUsed = matchPreds.filter(p => p.joker).length;
  const groupJokers = matchPreds.filter(p => p.joker && parseInt(p.matchId?.replace('M','')) <= 72).length;
  const koJokers = jokersUsed - groupJokers;

  const saveWinner = async () => {
    if (!winner) return;
    setSaving('winner');
    try {
      await api.saveWinner(user.sub, { winner });
      setSavedWinner(winner);
      setMsg({ type: 'success', text: `Winner prediction saved: ${winner}` });
    } catch (err) {
      setMsg({ type: 'error', text: err.message });
    } finally {
      setSaving('');
    }
  };

  const saveDefault = async () => {
    const h = parseInt(defaultHome);
    const a = parseInt(defaultAway);
    if (isNaN(h) || isNaN(a) || h < 0 || a < 0) {
      setMsg({ type: 'error', text: 'Enter valid scores (0 or more)' });
      return;
    }
    setSaving('default');
    try {
      await api.saveDefault(user.sub, { homeScore: h, awayScore: a });
      setSavedDefault({ homeScore: h, awayScore: a });
      setMsg({ type: 'success', text: `Default score saved: ${h}–${a}` });
    } catch (err) {
      setMsg({ type: 'error', text: err.message });
    } finally {
      setSaving('');
    }
  };

  const handleRequestLeague = async (e) => {
    e.preventDefault();
    if (!leagueRequestName.trim()) return;
    setLeagueRequesting(true);
    try {
      await api.requestLeague({ name: leagueRequestName.trim() });
      setLeagueRequestName('');
      setMsg({ type: 'success', text: 'League request submitted — the admin will review it.' });
    } catch (err) {
      setMsg({ type: 'error', text: err.message });
    } finally {
      setLeagueRequesting(false);
    }
  };

  if (loading) return <div className="loading-screen"><div className="spinner" /></div>;

  const liveResults = results.filter(r => r.status === 'LIVE' || r.status === 'HT');
  const totalMatches = ALL_MATCHES.length;
  const predictedCount = matchPreds.length;

  return (
    <div className="page">
      <h1 className="page-title">Dashboard</h1>
      <p className="page-subtitle">Welcome back, {user.name} — {format(now, 'EEEE d MMMM yyyy')}</p>

      {msg.text && (
        <div className={msg.type === 'error' ? 'error-msg' : 'success-msg'} style={{ marginBottom: '1.5rem' }}>
          {msg.text}
        </div>
      )}

      {/* Live banner */}
      {liveResults.length > 0 && (() => {
        const single = liveResults.length === 1 ? liveResults[0] : null;
        const match = single ? ALL_MATCHES.find(m => m.id === single.matchId) : null;
        const label = single && match
          ? `${match.home} ${single.homeScore}–${single.awayScore} ${match.away}${single.minute ? ` · ${single.minute}` : ''}`
          : `${liveResults.length} matches live now`;
        const dest = single ? `/matches?live=${single.matchId}` : '/matches';
        return (
          <button className="live-banner" onClick={() => navigate(dest)}>
            <span className="live-banner-dot" />
            <span className="live-banner-label">● {label}</span>
            <span className="live-banner-arrow">›</span>
          </button>
        );
      })()}

      {/* Stats row */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '1rem', marginBottom: '1.5rem' }}>
        <div className="stat">
          <div className="figure accent">
            {predictedCount}<span className="of">/{totalMatches}</span>
          </div>
          <div className="label">Predictions made</div>
        </div>
        <div className="stat">
          <div className="figure spark">{jokersUsed}<span className="of">/4</span></div>
          <div className="label">Jokers used</div>
          <div className="pips">
            {Array.from({ length: 4 }).map((_, i) => (
              <span key={i} className={`pip${i < jokersUsed ? ' used' : ''}`} />
            ))}
          </div>
        </div>
        <div className="stat">
          <div className="figure spark">{groupJokers}<span className="of">/2+</span></div>
          <div className="label">Group jokers</div>
          <div className="pips">
            {Array.from({ length: 2 }).map((_, i) => (
              <span key={i} className={`pip${i < groupJokers ? ' used' : ''}`} />
            ))}
          </div>
        </div>
        <div className="stat">
          <div className="figure spark">{koJokers}<span className="of">/2</span></div>
          <div className="label">KO jokers</div>
          <div className="pips">
            {Array.from({ length: 2 }).map((_, i) => (
              <span key={i} className={`pip${i < koJokers ? ' used' : ''}`} />
            ))}
          </div>
        </div>
      </div>

      {/* Winner prediction */}
      <div className="card" style={{ marginBottom: '1rem' }}>
        <div className="section-kicker" style={{ marginBottom: '0.75rem' }}>
          Tournament Winner
        </div>
        {winnerLocked && !winnerChangeable ? (
          <p style={{ color: 'var(--text-muted)', fontSize: '0.9rem' }}>
            Winner prediction is locked. Your pick: <strong style={{ color: 'var(--text)' }}>{savedWinner || 'none'}</strong>
          </p>
        ) : (
          <>
            <p style={{ color: 'var(--text-muted)', fontSize: '0.85rem', marginBottom: '0.75rem' }}>
              {!winnerLocked
                ? 'Pick your winner before M1 kicks off (11 Jun). Correct = 10 pts.'
                : `You can still change your winner before Round of 32 starts (28 Jun). Correct after change = 5 pts. Current: ${savedWinner || 'not set'}`
              }
            </p>
            <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'flex-end', flexWrap: 'wrap' }}>
              <div className="field" style={{ flex: 1, minWidth: '200px' }}>
                <label>Select winner</label>
                <select value={winner} onChange={e => setWinner(e.target.value)}>
                  <option value="">-- Choose a team --</option>
                  {TEAMS.map(t => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
              <button
                className="btn btn-primary"
                onClick={saveWinner}
                disabled={saving === 'winner' || !winner || winner === savedWinner}
              >
                {saving === 'winner' ? 'Saving…' : savedWinner ? 'Update Pick' : 'Save Pick'}
              </button>
            </div>
          </>
        )}
      </div>

      {/* Default score */}
      <div className="card" style={{ marginBottom: '1.5rem' }}>
        <div className="section-kicker" style={{ marginBottom: '0.75rem' }}>
          Default Score
        </div>
        <p style={{ color: 'var(--text-muted)', fontSize: '0.85rem', marginBottom: '0.75rem' }}>
          This score is used for any match where you haven't set a specific prediction before kick-off.
          {savedDefault && (
            <strong style={{ color: 'var(--text)' }}> Current default: {savedDefault.homeScore}–{savedDefault.awayScore}</strong>
          )}
        </p>
        <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'flex-end', flexWrap: 'wrap' }}>
          <div>
            <div style={{ fontSize: 'var(--t-xs)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--text-muted)', marginBottom: '0.4rem' }}>
              Score
            </div>
            <div className="score-input">
              <input
                type="number" min="0" max="20"
                value={defaultHome}
                onChange={e => setDefaultHome(e.target.value)}
                placeholder="0"
              />
              <span className="score-separator">–</span>
              <input
                type="number" min="0" max="20"
                value={defaultAway}
                onChange={e => setDefaultAway(e.target.value)}
                placeholder="0"
              />
            </div>
          </div>
          <button
            className="btn btn-primary"
            onClick={saveDefault}
            disabled={saving === 'default'}
          >
            {saving === 'default' ? 'Saving…' : savedDefault ? 'Update Default' : 'Set Default'}
          </button>
        </div>
      </div>

      {/* How to Play */}
      <div className="card" style={{ marginBottom: '1rem' }}>
        <div className="section-kicker" style={{ marginBottom: '1rem' }}>
          How to Play
        </div>

        <div style={{ marginBottom: '1rem' }}>
          <div style={{ fontSize: 'var(--t-xs)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--text-muted)', marginBottom: '0.5rem' }}>Scoring</div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '0.5rem' }}>
            {[
              { pts: '2 pts', label: 'Exact score + correct outcome' },
              { pts: '1 pt',  label: 'Correct outcome only (W/D/L)' },
              { pts: '×2',    label: 'Joker — doubles your match points' },
              { pts: '10 pts', label: 'Tournament winner (original pick)' },
              { pts: '5 pts',  label: 'Tournament winner (changed before R32)' },
            ].map(row => (
              <div key={row.pts} style={{ display: 'flex', gap: '0.5rem', alignItems: 'flex-start' }}>
                <span style={{ fontFamily: 'var(--font-mono)', fontSize: '1rem', color: 'var(--volt)', minWidth: 52, flexShrink: 0 }}>{row.pts}</span>
                <span style={{ color: 'var(--text-muted)', fontSize: '0.82rem', lineHeight: 1.4 }}>{row.label}</span>
              </div>
            ))}
          </div>
          <p style={{ color: 'var(--text-faint)', fontSize: 'var(--t-xs)', fontFamily: 'var(--font-mono)', marginTop: '0.5rem' }}>
            All points based on 90-minute result only — no extra time or penalties
          </p>
        </div>

        <div style={{ borderTop: '1px solid var(--line)', paddingTop: '0.75rem', marginBottom: '0.75rem' }}>
          <div style={{ fontSize: 'var(--t-xs)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--text-muted)', marginBottom: '0.5rem' }}>Jokers</div>
          <ul style={{ color: 'var(--text-muted)', fontSize: '0.85rem', paddingLeft: '1.25rem', lineHeight: 1.7, margin: 0 }}>
            <li>You have <strong style={{ color: 'var(--text)' }}>4 jokers</strong> total — doubles your points for that match</li>
            <li>Minimum <strong style={{ color: 'var(--text)' }}>2 must be used in the group stage</strong></li>
            <li>Maximum <strong style={{ color: 'var(--text)' }}>2 can be used in knockout stage</strong></li>
            <li>Unused group jokers <strong style={{ color: 'var(--danger)' }}>cannot</strong> be carried to knockout stage</li>
          </ul>
        </div>

        <div style={{ borderTop: '1px solid var(--line)', paddingTop: '0.75rem' }}>
          <div style={{ fontSize: 'var(--t-xs)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--text-muted)', marginBottom: '0.5rem' }}>Key deadlines</div>
          <ul style={{ color: 'var(--text-muted)', fontSize: '0.85rem', paddingLeft: '1.25rem', lineHeight: 1.7, margin: 0 }}>
            <li>Each prediction locks at <strong style={{ color: 'var(--text)' }}>kick-off</strong> — set them before the match starts</li>
            <li>Tournament winner pick locks at <strong style={{ color: 'var(--text)' }}>M1 kick-off (11 Jun)</strong> — 10 pts if correct</li>
            <li>Winner pick can be <strong style={{ color: 'var(--text)' }}>changed until R32 starts (28 Jun)</strong> — worth 5 pts if correct</li>
          </ul>
        </div>
      </div>

      {/* What's New */}
      <Link to="/changelog" style={{ display: 'block', textDecoration: 'none' }}>
        <div className="card" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '1rem' }}>
          <div>
            <div className="section-kicker" style={{ marginBottom: '0.2rem' }}>What's New</div>
            <div style={{ color: 'var(--text-muted)', fontSize: 'var(--t-sm)' }}>Recent changes to the app</div>
          </div>
          <span style={{ color: 'var(--text-faint)', fontSize: 'var(--t-lg)' }}>›</span>
        </div>
      </Link>

    </div>
  );
}
